import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaTrash, FaCheck, FaInfoCircle } from 'react-icons/fa';

const Step3CustomTableModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  memberIndex, 
  onSave,
  existingTable 
}) => {
  // Predefined column names as per user requirement
  const COLUMNS = [
    { name: 'Information / Documents Required', isMandatory: false },
    { name: 'Remarks by Client', isMandatory: false }
  ];

  const [rows, setRows] = useState([
    { adminValue: '', isMandatory: true }
  ]);

  useEffect(() => {
    if (isOpen) {
      if (existingTable && existingTable.customRows && existingTable.customRows.length > 0) {
        setRows(existingTable.customRows);
      } else if (existingTable && existingTable.rows && existingTable.rows.length > 0) {
        // Migration from old structure if needed
        setRows(existingTable.rows.map(row => ({
          adminValue: row[0] || '',
          isMandatory: existingTable.columns?.[0]?.isMandatory ?? true
        })));
      } else {
        setRows([{ adminValue: '', isMandatory: true }]);
      }
    }
  }, [isOpen, existingTable]);

  const addRow = () => {
    setRows([...rows, { adminValue: '', isMandatory: true }]);
  };

  const removeRow = (rowIndex) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== rowIndex));
  };

  const updateAdminValue = (rowIndex, value) => {
    const newRows = [...rows];
    newRows[rowIndex].adminValue = value;
    setRows(newRows);
  };

  const toggleMandatory = (rowIndex) => {
    const newRows = [...rows];
    newRows[rowIndex].isMandatory = !newRows[rowIndex].isMandatory;
    setRows(newRows);
  };

  const handleSave = () => {
    // Validate that at least some admin values are filled
    if (rows.some(r => !r.adminValue.trim())) {
      if (!window.confirm("Some rows have empty 'Information / Documents Required' values. Continue?")) {
        return;
      }
    }

    const customTable = {
      columns: COLUMNS.map((col, i) => ({
        ...col,
        // The first column is effectively mandatory if any row within it is marked mandatory
        // But the backend expects isMandatory on columns. We'll store row-level mandatory state in rows.
      })),
      rows: rows.map(r => [r.adminValue, '']), // Legacy compatibility if needed
      customRows: rows // New row-level mandatory structure
    };
    onSave(customTable);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-5 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <FaInfoCircle className="text-blue-200" />
              Redesign Step 3 Custom Table
            </h2>
            <p className="text-blue-100 text-sm mt-1 opacity-90">Define the exact information you require from the client.</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2.5 transition-all duration-200"
          >
            <FaTimes size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              <FaInfoCircle size={18} />
            </div>
            <div>
              <p className="text-xs text-blue-800 leading-relaxed font-semibold">
                Define the information or documents you require. Column 1 is for your requirements, Column 2 is for client remarks.
              </p>
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-bold text-gray-800 tracking-tight flex items-center gap-2">
                Table Structure
                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 italic">Admin Config</span>
              </h3>
              <button
                onClick={addRow}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-all duration-200 shadow-lg hover:shadow-indigo-200"
              >
                <FaPlus size={12} />
                Add Row
              </button>
            </div>

            <div className="overflow-hidden border border-gray-200 rounded-xl shadow-xl bg-white">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 tracking-wider w-12 text-center">#</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 tracking-wider">
                      Information / Documents Required
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 tracking-wider w-28">
                      Mandatory
                    </th>
                    <th className="px-5 py-3 text-center text-[11px] font-bold text-gray-400 tracking-wider w-20">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-4 py-2 text-center text-xs font-black text-gray-300">
                        {(rowIndex + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="px-5 py-2">
                        <textarea
                          rows={1}
                          value={row.adminValue}
                          onChange={(e) => updateAdminValue(rowIndex, e.target.value)}
                          placeholder="e.g., W-2 form, 1099-MISC..."
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs font-semibold transition-all resize-none overflow-hidden bg-gray-50/30"
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                          }}
                        />
                      </td>
                      <td className="px-5 py-2">
                        <label className="relative inline-flex items-center cursor-pointer scale-90">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={row.isMandatory}
                            onChange={() => toggleMandatory(rowIndex)}
                          />
                          <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                          <span className="ml-2 text-[10px] font-black text-gray-500 uppercase">{row.isMandatory ? 'Yes' : 'No'}</span>
                        </label>
                      </td>
                      <td className="px-5 py-2 text-center">
                        <button
                          onClick={() => removeRow(rowIndex)}
                          disabled={rows.length <= 1}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed group-hover:scale-110"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-8 py-4 flex justify-end gap-3 border-t border-gray-100 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.03)]">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-xs font-bold text-gray-400 border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-600 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white text-xs font-bold rounded-lg hover:from-indigo-700 hover:to-indigo-900 transition-all duration-300 shadow-lg hover:shadow-indigo-200 transform hover:-translate-y-0.5"
          >
            <FaCheck size={14} />
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step3CustomTableModal;
